import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  User, CalendarDays, DollarSign, FileText, Clock, Save, Loader2, Plus, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const LEAVE_TYPES = {
  yillik: "Yıllık", hastalik: "Hastalık", mazeret: "Mazeret",
  ucretsiz: "Ücretsiz", ebeveyn: "Ebeveyn", diger: "Diğer",
};

const LEAVE_STATUS_COLORS = {
  beklemede: "text-amber-500",
  onaylandi: "text-emerald-400",
  reddedildi: "text-rose-400",
  iptal: "text-slate-400",
};

const ADVANCE_STATUS_COLORS = {
  beklemede: "text-amber-500",
  onaylandi: "text-emerald-400",
  reddedildi: "text-rose-400",
  odendi: "text-blue-400",
};

const fmtMoney = (n) => (Number(n) || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * EmployeePortal — çalışan self-service sayfası.
 * Kullanıcının kendi employee_id kaydına bağlı verilerini gösterir.
 * Yönetici olmayan çalışanlar giriş yaptığında bu sayfayı kullanır.
 */
export default function EmployeePortal() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hr/portal/me");
      setMe(data);
    } catch (e) {
      setError(e?.response?.data?.detail || formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;
  }

  if (error || !me) {
    return (
      <div className="p-8">
        <Card className="rounded-sm border-amber-800 bg-amber-950/20 p-6 max-w-lg mx-auto">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-500 shrink-0" />
            <div>
              <div className="font-bold text-amber-300">Portala erişemiyorsunuz</div>
              <p className="text-sm text-muted-foreground mt-2">{error}</p>
              <p className="text-xs text-slate-500 mt-3">
                Yöneticinizin sizi bir çalışan kaydına bağlaması gerekiyor. Ayarlar → Kullanıcılar üzerinden
                hesabınıza <b>employee_id</b> atamak yeterlidir.
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const emp = me.employee;

  return (
    <div>
      <PageHeader
        title={`Merhaba, ${emp.name.split(" ")[0]}`}
        subtitle="Kişisel çalışan portalı — izin, avans, bordro ve puantajlarınız"
        breadcrumbs={["Ana Sayfa", "Portal"]}
      />
      <div className="p-6 lg:p-8 space-y-4 max-w-5xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <PortalStat label="Kalan Yıllık İzin" value={`${me.leave_remaining} gün`} />
          <PortalStat label="Aylık Maaş" value={emp.salary ? `₺${fmtMoney(emp.salary)}` : "—"} />
          <PortalStat label="Departman" value={emp.department || "—"} />
          <PortalStat label="Ünvan" value={emp.position || "—"} />
        </div>

        <Tabs defaultValue="izin">
          <TabsList className="rounded-sm bg-muted/40 border border-border">
            <TabsTrigger value="izin" data-testid="portal-tab-izin" className="rounded-sm">
              <CalendarDays className="w-3.5 h-3.5 mr-1" /> İzinlerim
            </TabsTrigger>
            <TabsTrigger value="avans" data-testid="portal-tab-avans" className="rounded-sm">
              <DollarSign className="w-3.5 h-3.5 mr-1" /> Avans Taleplerim
            </TabsTrigger>
            <TabsTrigger value="bordro" data-testid="portal-tab-bordro" className="rounded-sm">
              <FileText className="w-3.5 h-3.5 mr-1" /> Bordrolarım
            </TabsTrigger>
            <TabsTrigger value="puantaj" data-testid="portal-tab-puantaj" className="rounded-sm">
              <Clock className="w-3.5 h-3.5 mr-1" /> Puantajım
            </TabsTrigger>
          </TabsList>

          <TabsContent value="izin" className="mt-4"><PortalLeaves onChange={load} /></TabsContent>
          <TabsContent value="avans" className="mt-4"><PortalAdvances /></TabsContent>
          <TabsContent value="bordro" className="mt-4"><PortalPayroll /></TabsContent>
          <TabsContent value="puantaj" className="mt-4"><PortalAttendance /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function PortalStat({ label, value }) {
  return (
    <Card className="rounded-sm border-border p-3">
      <div className="text-[10px] uppercase font-mono tracking-widest text-muted-foreground">{label}</div>
      <div className="text-lg font-bold mt-1">{value}</div>
    </Card>
  );
}

function PortalLeaves({ onChange }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ leave_type: "yillik", start_date: "", end_date: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/hr/portal/leaves");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Tarihler zorunlu"); return; }
    setSaving(true);
    try {
      await api.post("/hr/portal/leaves", form);
      toast.success("İzin talebiniz iletildi");
      setOpen(false);
      setForm({ leave_type: "yillik", start_date: "", end_date: "", reason: "" });
      load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)} className="rounded-sm bg-blue-600 hover:bg-blue-500" data-testid="portal-new-leave">
          <Plus className="w-3.5 h-3.5 mr-1" /> Yeni İzin Talebi
        </Button>
      </div>
      <Card className="rounded-sm border-border overflow-hidden">
        {rows.length === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground">İzin talebiniz yok.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                <th className="text-left px-3 py-2">Tip</th>
                <th className="text-left px-3 py-2">Başlangıç</th>
                <th className="text-left px-3 py-2">Bitiş</th>
                <th className="text-right px-3 py-2">Gün</th>
                <th className="text-left px-3 py-2">Durum</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-1.5 text-xs">{LEAVE_TYPES[r.leave_type]}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.start_date}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.end_date}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs font-bold">{r.day_count}</td>
                  <td className={`px-3 py-1.5 text-xs font-bold ${LEAVE_STATUS_COLORS[r.status]}`}>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle>Yeni İzin Talebi</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">İzin Tipi</Label>
              <Select value={form.leave_type} onValueChange={(v) => setForm((p) => ({ ...p, leave_type: v }))}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="portal-leave-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(LEAVE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Başlangıç *</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))} className="rounded-sm mt-1" data-testid="portal-leave-start" />
              </div>
              <div>
                <Label className="text-xs">Bitiş *</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))} className="rounded-sm mt-1" data-testid="portal-leave-end" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Sebep</Label>
              <Textarea rows={2} value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))} className="rounded-sm mt-1" data-testid="portal-leave-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={create} disabled={saving} className="rounded-sm bg-emerald-600 hover:bg-emerald-500" data-testid="portal-leave-submit">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Talep Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PortalAdvances() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: 0, currency: "TRY", reason: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/hr/portal/advances"); setRows(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (form.amount <= 0) { toast.error("Tutar 0'dan büyük olmalı"); return; }
    setSaving(true);
    try {
      await api.post("/hr/portal/advances", form);
      toast.success("Avans talebiniz iletildi");
      setOpen(false);
      setForm({ amount: 0, currency: "TRY", reason: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)} className="rounded-sm bg-blue-600 hover:bg-blue-500" data-testid="portal-new-advance">
          <Plus className="w-3.5 h-3.5 mr-1" /> Yeni Avans Talebi
        </Button>
      </div>
      <Card className="rounded-sm border-border overflow-hidden">
        {rows.length === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground">Avans talebiniz yok.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                <th className="text-left px-3 py-2">Tarih</th>
                <th className="text-right px-3 py-2">Tutar</th>
                <th className="text-left px-3 py-2">Sebep</th>
                <th className="text-left px-3 py-2">Durum</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.request_date}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-bold">{r.currency === "TRY" ? "₺" : (r.currency === "USD" ? "$" : "€")}{fmtMoney(r.amount)}</td>
                  <td className="px-3 py-1.5 text-xs">{r.reason || "—"}</td>
                  <td className={`px-3 py-1.5 text-xs font-bold ${ADVANCE_STATUS_COLORS[r.status]}`}>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle>Yeni Avans Talebi</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Tutar *</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: Number(e.target.value) || 0 }))} className="rounded-sm mt-1" data-testid="portal-adv-amount" />
              </div>
              <div>
                <Label className="text-xs">Para Birimi</Label>
                <Select value={form.currency} onValueChange={(v) => setForm((p) => ({ ...p, currency: v }))}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="portal-adv-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRY">₺ TRY</SelectItem>
                    <SelectItem value="USD">$ USD</SelectItem>
                    <SelectItem value="EUR">€ EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Sebep</Label>
              <Textarea rows={2} value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))} className="rounded-sm mt-1" data-testid="portal-adv-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={create} disabled={saving} className="rounded-sm bg-emerald-600 hover:bg-emerald-500" data-testid="portal-adv-submit">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Talep Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PortalPayroll() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/hr/portal/payroll").then(({ data }) => setRows(data || [])).catch((e) => toast.error(formatApiError(e)));
  }, []);
  return (
    <Card className="rounded-sm border-border overflow-hidden">
      {rows.length === 0 ? (
        <div className="text-center py-6 text-sm text-muted-foreground">Bordro kaydınız yok.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-[10px] uppercase font-mono text-muted-foreground">
              <th className="text-left px-3 py-2">Dönem</th>
              <th className="text-right px-3 py-2">Brüt</th>
              <th className="text-right px-3 py-2">Net</th>
              <th className="text-left px-3 py-2">Ödendi?</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((p) => (
              <tr key={p.id}>
                <td className="px-3 py-1.5 font-mono font-bold">{p.period}</td>
                <td className="px-3 py-1.5 text-right font-mono">₺{fmtMoney(p.gross_salary)}</td>
                <td className="px-3 py-1.5 text-right font-mono font-bold text-emerald-400">₺{fmtMoney(p.net_salary)}</td>
                <td className="px-3 py-1.5 text-xs">{p.paid ? <span className="text-emerald-400">✓ {p.paid_at?.slice(0, 10)}</span> : <span className="text-amber-500">Bekliyor</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function PortalAttendance() {
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const [y, m] = period.split("-");
    const start = `${period}-01`;
    const ny = m === "12" ? Number(y) + 1 : Number(y);
    const nm = m === "12" ? "01" : String(Number(m) + 1).padStart(2, "0");
    const end = new Date(`${ny}-${nm}-01`);
    end.setDate(end.getDate() - 1);
    api.get(`/hr/portal/attendance?start=${start}&end=${end.toISOString().slice(0, 10)}`)
      .then(({ data }) => setRows(data || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, [period]);

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Dönem</Label>
        <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="rounded-sm mt-1 w-40" />
      </div>
      <Card className="rounded-sm border-border overflow-hidden">
        {rows.length === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground">Bu ay için kayıt yok.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                <th className="text-left px-3 py-2">Tarih</th>
                <th className="text-left px-3 py-2">Giriş</th>
                <th className="text-left px-3 py-2">Çıkış</th>
                <th className="text-right px-3 py-2">Çalışma</th>
                <th className="text-right px-3 py-2">F. Mesai</th>
                <th className="text-left px-3 py-2">Durum</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.date}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.check_in || "—"}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.check_out || "—"}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">{Math.floor(r.worked_minutes / 60)}s {r.worked_minutes % 60}dk</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs text-blue-400">{r.overtime_minutes > 0 ? `${Math.floor(r.overtime_minutes / 60)}s ${r.overtime_minutes % 60}dk` : "—"}</td>
                  <td className="px-3 py-1.5 text-xs uppercase font-mono">{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
